// useEtiquetaEditor - S47.1 / S47.2 / S48.1.
// Estado del editor de etiquetas con persistencia minima en localStorage
// (S47.2) + accion setFields para aplicacion bulk de un layout S48.1.
//
// State:
//   sizeId: id del tamano actual (ej. 'avery-5160').
//   fields: Array<Field> colocados sobre la etiqueta.
//   selectedId: id del field seleccionado o null.
//
// Field shape:
//   { id, type, xMm, yMm, wMm, hMm, bindKey?: string|null }
//
// Acciones expuestas:
//   setSizeId(id)
//   addField({ type, xMm, yMm, bindKey?, wMm?, hMm? })
//   removeField(id)
//   selectField(id|null)
//   updateField(id, patch)
//   clearFields()
//   setFields(arr)        // S48.1: bulk replace
import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_SIZE_ID,
  FIELD_DEFAULT_W_MM,
  FIELD_DEFAULT_H_MM,
  FIELD_TYPES,
} from '../constants';
import { newFieldId, snapMm, clampMm, findSize } from '../utils';
import { saveState, loadState } from '../persistence';

function _initial() {
  const persisted = loadState();
  if (persisted) {
    return {
      sizeId: persisted.sizeId || DEFAULT_SIZE_ID,
      fields: persisted.fields || [],
    };
  }
  return { sizeId: DEFAULT_SIZE_ID, fields: [] };
}

export default function useEtiquetaEditor() {
  const init = _initial();
  const [sizeId, _setSizeId] = useState(init.sizeId);
  const [fields, _setFields] = useState(init.fields);
  const [selectedId, setSelectedId] = useState(null);

  // Persistir en cada cambio de sizeId/fields.
  useEffect(() => {
    saveState({ sizeId: sizeId, fields: fields });
  }, [sizeId, fields]);

  const setSizeId = useCallback((id) => {
    _setSizeId(id);
    setSelectedId(null);
  }, []);

  const addField = useCallback((opts) => {
    const type = opts && opts.type;
    if (!type || FIELD_TYPES.indexOf(type) === -1) return null;
    const sz = findSize(sizeId);
    const wMm = (opts && Number(opts.wMm)) || FIELD_DEFAULT_W_MM;
    const hMm = (opts && Number(opts.hMm)) || FIELD_DEFAULT_H_MM;
    const xRaw = (opts && Number(opts.xMm)) || 0;
    const yRaw = (opts && Number(opts.yMm)) || 0;
    const xMm = clampMm(snapMm(xRaw), 0, Math.max(0, sz.wMm - wMm));
    const yMm = clampMm(snapMm(yRaw), 0, Math.max(0, sz.hMm - hMm));
    const bindKey = (opts && typeof opts.bindKey === 'string') ? opts.bindKey : null;
    const f = {
      id: newFieldId(),
      type: type,
      xMm: xMm,
      yMm: yMm,
      wMm: wMm,
      hMm: hMm,
      bindKey: bindKey,
    };
    _setFields((arr) => arr.concat([f]));
    setSelectedId(f.id);
    return f;
  }, [sizeId]);

  const removeField = useCallback((id) => {
    _setFields((arr) => arr.filter((f) => f.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const selectField = useCallback((id) => {
    setSelectedId(id || null);
  }, []);

  const updateField = useCallback((id, patch) => {
    if (!id || !patch) return;
    _setFields((arr) => arr.map((f) => (f.id === id ? Object.assign({}, f, patch) : f)));
  }, []);

  const clearFields = useCallback(() => {
    _setFields([]);
    setSelectedId(null);
  }, []);

  // S48.1: setFields bulk replace.
  // Reemplaza completamente el array de fields. Cada item del array debe
  // ser un Field shape valido. No clamp interno: el caller debe ya haber
  // calculado posiciones validas para el sizeId actual.
  const setFields = useCallback((arr) => {
    const safe = Array.isArray(arr) ? arr.slice() : [];
    _setFields(safe);
    setSelectedId(null);
  }, []);

  return {
    sizeId,
    fields,
    selectedId,
    setSizeId,
    addField,
    removeField,
    selectField,
    updateField,
    clearFields,
    setFields,
  };
}
